import { create } from 'zustand';

export type TaskStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'WAITING'
  | 'DONE'
  | 'CLAIMED';
export type TaskType =
  | 'sponsors'
  | 'quest-daily'
  | 'partners'
  | 'starter-quest'
  | 'advanced-quest'
  | 'expert-quest'
  | 'daily-bonus';
export type TaskProvider =
  | 'PARTNERS'
  | 'DAILY'
  | 'SPONSORS'
  | 'BARZHA'
  | 'TAPPADS'
  | 'DREAMCOIN_CLICK'
  | 'DREAMCOIN_SUBSCRIBE'
  | 'BARZHA_CLICK'
  | 'FLYER';

export interface Task {
  id: string | number;
  title: string;
  image?: string;
  centerIcon?: string;
  icon: string;
  type: string;
  group?: 'individual' | 'combined';
  description?: string;
  rewards?: {
    dc?: number;
    tickets?: number;
    [key: string]: number | undefined;
  };
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'WAITING' | 'DONE' | 'CLAIMED';
  buttonText: string;
  link?: string;
  is_subscribed?: boolean;
  progress?: {
    current: number;
    total: number;
  };
  provider: TaskProvider;
  responseType?: string;
  completed_elsewhere?: boolean;
  // meta?: {
  //   adId?: number;
  //   ads?: any[];
  // };
  // onClick?: () => Promise<boolean>;
}

export interface TraffyTask {
  id: string;
  image_url: string;
  is_completable: boolean;
  is_test: boolean;
  link: string;
  target: string;
  title: string;
}

export interface BarzhaClickAd {
  id: number;
  title: string;
  description?: string;
  image_url?: string;
  reward_amount?: number;
  reward_type?: string;
  exchange_ad_id?: number;
  image_href?: string;
  status?: string;
  visibility?: boolean;
}

export interface BarzhaClickResponse {
  status: string;
  data: BarzhaClickAd[];
  started_tasks?: BarzhaClickAd[];
  webscoket_channel_name?: string;
  webscoket_client_token?: string;
  error?: string;
}

export interface TaskStatusItem {
  status: string;
  task_id: string;
  type: string;
  visibility: boolean;
}

type TasksStore = {
  tasks: Task[];
  setTasks: (arg: any[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  showNotification: boolean;
  setShowNotification: (show: boolean) => void;
  triggerNotification: (isModalOpen?: boolean) => void;
  refreshSponsorsTasks: () => void;
  setRefreshSponsorsTasks: (callback: () => void) => void;
  notificationTimer: NodeJS.Timeout | null;
  setNotificationTimer: (timer: NodeJS.Timeout | null) => void;
  clearNotificationTimer: () => void;
  delayedNotification: boolean;
  setDelayedNotification: (delayed: boolean) => void;
  showDelayedNotification: () => void;
  lastSponsorsFetchTime: number;
  setLastSponsorsFetchTime: (time: number) => void;
  canRefreshSponsors: () => boolean;
  // Состояние таймера кнопки обновления
  isRefreshing: boolean;
  setIsRefreshing: (refreshing: boolean) => void;
  timeLeft: number;
  setTimeLeft: (time: number) => void;
  startRefreshTimer: () => void;
  stopRefreshTimer: () => void;
  // Флаг для контроля fetch после перезагрузки
  needInitialFetch: boolean;
  setNeedInitialFetch: (need: boolean) => void;
  refreshSponsorsTasksCallback: (() => void) | undefined;
};

export const useTasksStore = create<TasksStore>((set, get) => ({
  tasks: [],
  setTasks: (arg) => set({ tasks: arg }),
  isLoading: false,
  setIsLoading: (loading) => set({ isLoading: loading }),
  showNotification: false,
  setShowNotification: (show) => set({ showNotification: show }),
  notificationTimer: null,
  setNotificationTimer: (timer) => set({ notificationTimer: timer }),
  clearNotificationTimer: () => {
    const state = get();
    if (state.notificationTimer) {
      clearTimeout(state.notificationTimer);
      set({ notificationTimer: null });
    }
  },
  delayedNotification: false,
  setDelayedNotification: (delayed) => set({ delayedNotification: delayed }),
  showDelayedNotification: () => {
    const state = get();
    if (state.delayedNotification) {
      set({
        showNotification: true,
        delayedNotification: false,
      });

      // Автоматически скрываем уведомление через 5 секунд
      const timer = setTimeout(() => {
        set({ showNotification: false });
      }, 5000);

      set({ notificationTimer: timer });
    }
  },
  triggerNotification: (isModalOpen = false) => {
    const state = get();

    if (isModalOpen) {
      set({ delayedNotification: true });
    } else {
      set({ showNotification: true });

      // Автоматически скрываем уведомление через 5 секунд
      const timer = setTimeout(() => {
        set({ showNotification: false });
      }, 5000);

      set({ notificationTimer: timer });
    }
  },
  refreshSponsorsTasksCallback: undefined,
  refreshSponsorsTasks: () => {
    const state = get();
    if (state.refreshSponsorsTasksCallback) {
      state.refreshSponsorsTasksCallback();
    }
  },
  setRefreshSponsorsTasks: (callback) =>
    set({ refreshSponsorsTasksCallback: callback }),
  lastSponsorsFetchTime: 0,
  setLastSponsorsFetchTime: (time) => set({ lastSponsorsFetchTime: time }),
  canRefreshSponsors: () => {
    const state = get();
    const timeSinceLastFetch = Date.now() - state.lastSponsorsFetchTime;
    const FETCH_COOLDOWN = 60000; // 60 секунд кулдаун (соответствует автоматическому интервалу)
    return timeSinceLastFetch >= FETCH_COOLDOWN;
  },
  // Состояние таймера кнопки обновления
  isRefreshing: false,
  setIsRefreshing: (refreshing) => set({ isRefreshing: refreshing }),
  timeLeft: 0,
  setTimeLeft: (time) => set({ timeLeft: time }),
  startRefreshTimer: () => {
    const state = get();
    if (state.isRefreshing) return; // Уже запущен

    set({ isRefreshing: true, timeLeft: 30 });
    const timer = setInterval(() => {
      const currentState = get();
      if (currentState.timeLeft <= 1) {
        clearInterval(timer);
        set({ isRefreshing: false, timeLeft: 0 });
      } else {
        set({ timeLeft: currentState.timeLeft - 1 });
      }
    }, 1000);
  },
  stopRefreshTimer: () => {
    set({ isRefreshing: false, timeLeft: 0 });
  },
  // Флаг для контроля fetch после перезагрузки
  needInitialFetch: false,
  setNeedInitialFetch: (need) => set({ needInitialFetch: need }),
}));
